import { format, formatDistanceToNowStrict, isToday, parseISO } from 'date-fns';

/**
 * Short, time-of-day relative formatter tuned for order cards.
 * Today → "just now" / "5m ago" / "14:32"
 * Older → "Mar 12, 14:32"
 */
export function formatRelativeTime(iso: string): string {
  const d = safeParse(iso);
  if (!d) return '';
  if (isToday(d)) {
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60_000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    return format(d, 'HH:mm');
  }
  return format(d, 'MMM d, HH:mm');
}

export function formatFullDateTime(iso: string): string {
  const d = safeParse(iso);
  if (!d) return '';
  return format(d, "MMM d, yyyy 'at' HH:mm");
}

export function formatTimeOfDay(iso: string): string {
  const d = safeParse(iso);
  if (!d) return '';
  return format(d, 'HH:mm');
}

/**
 * Absolute timestamp for timeline rows: HH:mm when the event happened today,
 * "MMM d, HH:mm" otherwise. Unlike formatRelativeTime, never returns a
 * "Xm ago" relative string — timelines need stable anchors.
 */
export function formatClockOrDate(iso: string): string {
  const d = safeParse(iso);
  if (!d) return '';
  if (isToday(d)) return format(d, 'HH:mm');
  return format(d, 'MMM d, HH:mm');
}

/**
 * Rounded, compact duration between two ISO timestamps — "23m", "1h 05m".
 * Returns null when inputs are invalid or end < start.
 */
export function formatDurationBetween(
  startIso: string | undefined | null,
  endIso: string | undefined | null,
): string | null {
  if (!startIso || !endIso) return null;
  const s = Date.parse(startIso);
  const e = Date.parse(endIso);
  if (Number.isNaN(s) || Number.isNaN(e) || e < s) return null;
  const totalMin = Math.round((e - s) / 60_000);
  if (totalMin < 1) return '< 1m';
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, '0')}m`;
}

/**
 * Live dwell formatter — how long since `sinceIso`, tuned for a 1s ticker.
 *   < 1m   → "12s"
 *   < 1h   → "5m 12s"
 *   >= 1h  → "2h 05m"
 */
export function formatDwell(nowMs: number, sinceIso: string | undefined): string {
  if (!sinceIso) return '';
  const t = Date.parse(sinceIso);
  if (Number.isNaN(t)) return '';
  const totalSec = Math.max(0, Math.floor((nowMs - t) / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

export function formatDistanceOrBlank(iso: string): string {
  const d = safeParse(iso);
  if (!d) return '';
  try {
    return formatDistanceToNowStrict(d);
  } catch {
    return '';
  }
}

function safeParse(iso: string): Date | null {
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

/** Currency symbol lookup — shared between cards + stats. */
export function currencySymbol(currency: string | undefined): string {
  if (!currency) return '€';
  switch (currency.toUpperCase()) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    default:
      return '';
  }
}

export function formatMoney(
  amount: number | null | undefined,
  currency: string | undefined = 'EUR'
): string {
  if (amount == null) return '—';
  return `${currencySymbol(currency)}${amount.toFixed(2)}`;
}
